import { Component, Inject, InjectionToken, EventEmitter, Output } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ItemToSell, Category } from 'src/app/_services';

export const SIDENAV_CATEGORIES = new InjectionToken<string[]>('SIDENAV_CATEGORIES');

class CategoryTreeNode {
  constructor(readonly code: string, readonly name: string, readonly fullName: string, readonly children: CategoryTreeNode[] = []) {}
}

type FilterNames = 'category';

export class Filters {
  constructor(public category?: Category, public owner?: string) {}
}

@Component({
  selector: 'search-sidenav',
  templateUrl: './search-sidenav.component.html',
  styleUrls: ['./search-sidenav.component.scss']
})
export class SearchSidenavComponent {

  categoryTree: CategoryTreeNode[];
  currentQuery: string | undefined;
  appliedFilters: Filters = new Filters();
  topLevelCategories = ItemToSell.TOP_LEVEL_CATEGORIES;
  categories: Category[];
  
  @Output() onNavigationCompleted: EventEmitter<void> = new EventEmitter();

  constructor(
    private router: Router,
    activatedRoute: ActivatedRoute,
    @Inject(SIDENAV_CATEGORIES) categories: Category[]) {
    
    this.categories = categories.length === 0 ? this.topLevelCategories : categories;
    
    activatedRoute.queryParams.subscribe(queryParams => {
      this.currentQuery = queryParams["q"];
      this.appliedFilters.category = queryParams["categoryCode"] ? this.getCategoryForCode(queryParams["categoryCode"]) : undefined;
      this.appliedFilters.owner = queryParams["owner"];
    });

    this.categoryTree = this.buildTree(this.categories);
  }
  
  private getCategoryForCode(code: string): Category | undefined {
    return this.categories.find(category => category.code === code);
  }
  
  hasSubCategories(): boolean {
    
    if (!this.categoryTree) {
      return false;
    }
    
    return !!this.categoryTree.find(node => node.children.length > 0);
  }
  
  searchForCategory(categoryCode: string) {
    this.appliedFilters.category = this.getCategoryForCode(categoryCode);    
    this.search();
  }
  
  search() {

    this.router.navigate(["/"], { queryParams: { 
      categoryCode: this.appliedFilters.category?.code,
      owner: this.appliedFilters.owner,
      q: this.currentQuery 
    }});
    
    this.onNavigationCompleted.emit();
  }

  onFilterRemoved(filterName: FilterNames) {
    this.appliedFilters[filterName] = undefined;
    this.search();
  }
  
  private buildTree(categories: Category[]): CategoryTreeNode[] {
    
    categories.sort((a, b) => {
      return a.name.split('>').length - b.name.split('>').length;
    });
    
    const topLevelNodes: CategoryTreeNode[] = [];
    const treeNodes = new Map<string, CategoryTreeNode>();
    
    categories.forEach(category => {
      
      const categoryHierarchyParts = category.name.split('>');
      const categoryNameWithGuaranteedSeparators = categoryHierarchyParts.map(level => level.trim()).join(" > ");
      const deepestCategoryName = categoryHierarchyParts.pop()!.trim();
      const categoryParent = categoryHierarchyParts.map(level => level.trim()).join(" > ");
      const existingNode = treeNodes.get(categoryNameWithGuaranteedSeparators);
      
      if (!existingNode) {
        
        const parentNode = treeNodes.get(categoryParent);
        const currentNode = new CategoryTreeNode(category.code, deepestCategoryName, category.name);
        
        treeNodes.set(categoryNameWithGuaranteedSeparators, currentNode);
        
        if (parentNode) {
          parentNode.children.push(currentNode);
        } else {
          topLevelNodes.push(currentNode);
        }
      }
    });
    
    return topLevelNodes;
  }
  
  searchByDispatchedFrom() {
    
    let query = this.currentQuery ? this.currentQuery + ' ' : '';
    
    if (!query.includes('EdasimüügiksListxLadu')) {
      query += 'EdasimüügiksListxLadu';
    }
    
    this.router.navigate(["/"], { queryParams: { 
      categoryCode: this.appliedFilters.category?.code,
      owner: this.appliedFilters.owner,
      q: query
    }});

    this.onNavigationCompleted.emit();
  }
}
